import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api-client';

export interface InventoryRecord {
  id: number;
  unique_id: string;
  oms_identifier: string | null;
  location_description: string | null;
  media_format: string | null;
  latitude: number | null;
  longitude: number | null;
  is_digital: boolean;
  unit: string | null;
  market: string | null;
  size: string | null;
  illumination: string | null;
  illumination_hours: string | null;
  facing: string | null;
  primary_read: string | null;
  num_faces: number | null;
  net_install_cost: number | null;
  net_copy_change_cost: number | null;
  net_production_cost: number | null;
  production_forced: boolean;
  net_rate: number | null;
  rate_cycle: string | null;
  digital_spot_length: number | null;
  number_of_spots: number | null;
  image_url: string | null;
  spec_sheet_url: string | null;
  impressions_18_plus: number | null;
  marketing_info: string | null;
  production_spec: string | null;
  package_name: string | null;
  restrictions: string | null;
  enabled_for_automation: boolean;
  active_date: string | null;
  inactive_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface CsvLoadResult {
  recordsLoaded: number;
  rowErrors: Array<{ row: number; field: string; message: string }>;
  totalRowsProcessed: number;
}

export interface ValidationResult {
  valid: boolean;
  matchSource: 'product_catalog' | 'inventory' | null;
  matchedMarket?: string;
  needsDisambiguation?: boolean;
  markets?: string[];
  error?: string;
}

/**
 * Hook to fetch paginated inventory records
 */
export function useInventoryRecords(page: number, pageSize: number) {
  return useQuery({
    queryKey: ['inventory', page, pageSize],
    queryFn: async () => {
      const response = await get<{
        success: boolean;
        data: { records: InventoryRecord[]; page: number; pageSize: number };
      }>(`/admin/inventory?page=${page}&pageSize=${pageSize}`);
      return response.data;
    },
  });
}

/**
 * Hook to fetch total inventory record count
 */
export function useInventoryCount() {
  return useQuery({
    queryKey: ['inventoryCount'],
    queryFn: async () => {
      const response = await get<{
        success: boolean;
        data: { count: number };
      }>('/admin/inventory/count');
      return response.data.count;
    },
  });
}

/**
 * Hook to upload inventory CSV file
 */
export function useUploadInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await post<
        { success: boolean; data: CsvLoadResult },
        FormData
      >('/admin/inventory/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 min — large CSV files need time for parsing + batch inserts
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventoryCount'] });
    },
  });
}

/**
 * Hook to validate a Product/Inventory ID via dual-ID lookup
 * Market is optional — when omitted, performs cross-market lookup
 * Enabled when id is present (market no longer required to trigger)
 */
export function useValidateProductId(id: string, market?: string) {
  return useQuery({
    queryKey: ['validateProduct', id, market ?? ''],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (market) params.set('market', market);
      const qs = params.toString();
      const url = `/validate-product/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`;
      const response = await get<{
        success: boolean;
        data: ValidationResult;
      }>(url);
      return response.data;
    },
    enabled: !!id,
  });
}